import { Search, SlidersHorizontal } from "lucide-react";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { publicApi } from "../../api/client";
import EmptyState from "../../components/common/EmptyState";
import LoadingState from "../../components/common/LoadingState";
import Footer from "../../components/feature/Footer";
import Navbar from "../../components/feature/Navbar";
import EventCard from "../../components/public/EventCard";
import type { EventItem } from "../../types/api";

const defaultCategories = ["all", "Premiere", "Festival", "Community", "Classic", "Documentary"];
type EventPeriod = "upcoming" | "past";

const pageCopy: Record<EventPeriod, { eyebrow: string; title: string; description: string; empty: string }> = {
  upcoming: {
    eyebrow: "AAN Upcoming Events",
    title: "Discover African Art Network experiences you can book now.",
    description:
      "Search upcoming productions, stage events, cinema experiences, and cultural programmes from African Art Network.",
    empty: "Try another category, widen the search, or check back when more AAN events are published.",
  },
  past: {
    eyebrow: "AAN Past Events",
    title: "Explore past African Art Network experiences.",
    description:
      "Browse completed productions, stage events, cinema experiences, and cultural programmes from African Art Network.",
    empty: "Try another category, widen the search, or check back as past AAN events are added.",
  },
};

export default function ScreeningsPage({ period = "upcoming" }: { period?: EventPeriod }) {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const deferredSearch = useDeferredValue(searchQuery);
  const copy = pageCopy[period];

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    void publicApi
      .listEvents({
        search: deferredSearch,
        category: selectedCategory === "all" ? undefined : selectedCategory,
        period,
      })
      .then(({ events: results }) => setEvents(results))
      .catch((requestError) =>
        setError(requestError instanceof Error ? requestError.message : "Could not load AAN events")
      )
      .finally(() => setIsLoading(false));
  }, [deferredSearch, period, selectedCategory]);

  const categories = useMemo(
    () => Array.from(new Set([...defaultCategories, ...events.map((event) => event.category)])).filter(Boolean),
    [events]
  );

  return (
    <div className="aan-ticket-flow min-h-screen bg-black text-white">
      <Navbar />

      <section className="relative overflow-hidden pb-14 pt-36">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[url('/assets/hero-bg.jpeg')] bg-cover bg-center opacity-25"
          aria-hidden="true"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/20 via-black to-black" aria-hidden="true" />
        <div className="section-frame">
          <div className="surface-card relative overflow-hidden p-6 sm:p-8 lg:p-10">
            <div>
              <div className="mb-3 font-display text-xs font-semibold uppercase tracking-[0.24em] text-aan-bright">{copy.eyebrow}</div>
              <h1 className="text-5xl font-black uppercase leading-none text-white">
                {copy.title}
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-muted-foreground">
                {copy.description}
              </p>
            </div>

            <div className="mt-8 grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
              <div className="flex items-center gap-3 rounded-full border border-line bg-surface px-5 py-4 shadow-sm">
                <Search size={18} className="text-slate-400" />
                <input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by event title, city, or venue"
                  className="w-full bg-transparent text-sm text-muted-foreground outline-none placeholder:text-slate-400"
                />
              </div>
              <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide">
                <div className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-3 text-sm font-semibold text-muted-foreground">
                  <SlidersHorizontal size={16} />
                  Filter
                </div>
                {categories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => setSelectedCategory(category)}
                    className={`rounded-full px-4 py-3 text-sm font-semibold transition ${
                      selectedCategory === category ? "bg-aan-green text-black" : "bg-surface text-muted-foreground hover:bg-surface-muted"
                    }`}
                  >
                    {category === "all" ? "All" : category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="section-frame">
          <div className="mb-6 flex items-center justify-between">
            <div className="text-sm font-semibold text-muted-foreground">
              {isLoading ? "Loading" : events.length} event{events.length === 1 ? "" : "s"} found
            </div>
          </div>

          {isLoading ? (
            <LoadingState label="Loading AAN events..." />
          ) : error ? (
            <div className="surface-card px-6 py-12 text-center text-sm font-semibold text-red-500">{error}</div>
          ) : events.length === 0 ? (
            <EmptyState
              title="No events match that filter"
              description={copy.empty}
            />
          ) : (
            <div className="grid gap-6 lg:grid-cols-3">
              {events.map((event) => (
                <EventCard key={event.id} event={event} mode={period === "past" ? "past" : "default"} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
